import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getEventDetail } from './events';
import type { EventDetailDto } from '@/services/platform/api/model';

export interface UseEventDetailResult {
  event: EventDetailDto | undefined;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches full event detail by public GUID. Mirrors `useExploreVenueDetail` —
 * a missing/inaccessible event (404) resolves to `event: undefined`, which the
 * screen renders as `NotFoundError`.
 */
export function useEventDetail(id?: string): UseEventDetailResult {
  const locale = useLocaleStore(state => state.locale);
  const [event, setEvent] = useState<EventDetailDto | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(!!id);

  const refetch = useCallback(async () => {
    if (!id) {
      setEvent(undefined);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getEventDetail(id, locale);
      setEvent(result);
    } finally {
      setIsLoading(false);
    }
  }, [id, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { event, isLoading, refetch };
}
